import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Cookie, Scale, Mail, MapPin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const LEGAL_CONTENT = {
  terms: {
    title: "Conditions Générales d'Utilisation",
    icon: FileText,
    lastUpdated: "1er Février 2026",
    sections: [
      {
        title: "1. Acceptation des conditions",
        content: `En accédant et en utilisant la plateforme MHARHER, vous acceptez d'être lié par les présentes Conditions Générales d'Utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre service.

MHARHER est une plateforme dédiée à la célébration et au partage de la cuisine tunisienne. Notre mission est de préserver et promouvoir le patrimoine culinaire tunisien.`
      },
      {
        title: "2. Description du service",
        content: `MHARHER offre les services suivants :
• Partage de recettes tunisiennes authentiques
• Système de gamification avec points BARKA
• Marketplace de chefs privés
• Défis culinaires communautaires
• Abonnements créateurs
• Contenu culturel et éducatif

Le service est disponible sur le web et via applications mobiles (iOS/Android).`
      },
      {
        title: "3. Inscription et compte utilisateur",
        content: `Pour utiliser MHARHER, vous devez :
• Avoir au moins 16 ans
• Fournir des informations exactes lors de l'inscription
• Maintenir la confidentialité de vos identifiants
• Ne pas créer plusieurs comptes
• Ne pas partager votre compte avec des tiers

Vous êtes responsable de toute activité sur votre compte.`
      },
      {
        title: "4. Contenu utilisateur",
        content: `En publiant du contenu sur MHARHER, vous :
• Garantissez détenir les droits sur ce contenu
• Accordez à MHARHER une licence non-exclusive pour l'afficher
• Acceptez que le contenu soit visible par d'autres utilisateurs
• Vous engagez à respecter les droits d'auteur

Contenu interdit :
• Contenu illégal, diffamatoire ou offensant
• Spam et publicité non autorisée
• Usurpation d'identité
• Contenu violent ou discriminatoire`
      },
      {
        title: "5. Système BARKA et monétisation",
        content: `Les points BARKA :
• Sont une mesure de réputation non monnayable
• Sont gagnés par des actions légitimes sur la plateforme
• Peuvent être perdus en cas de violation des règles

La monétisation (TND) :
• Est réservée aux créateurs vérifiés
• Nécessite une vérification KYC pour les retraits
• Est soumise à une commission plateforme (20% standard)
• Les paiements sont traités via Stripe`
      },
      {
        title: "6. Chefs privés et réservations",
        content: `Pour les chefs privés :
• Vous devez fournir des informations exactes sur vos qualifications
• Vous êtes responsable de la qualité de vos services
• Une commission de 15% s'applique sur chaque réservation
• Vous devez respecter les réservations confirmées

Pour les clients :
• Les réservations sont soumises à un acompte de 30%
• Les annulations moins de 48h avant perdent l'acompte
• Les litiges sont gérés par notre service support`
      },
      {
        title: "7. Propriété intellectuelle",
        content: `MHARHER et son contenu (logo, design, code) sont protégés par le droit d'auteur tunisien et international.

Les recettes partagées restent la propriété de leurs auteurs, mais une licence d'affichage est accordée à MHARHER.

Les "recettes familiales" premium bénéficient d'une protection renforcée et ne peuvent être partagées hors de la plateforme.`
      },
      {
        title: "8. Limitation de responsabilité",
        content: `MHARHER ne peut être tenu responsable :
• Des contenus publiés par les utilisateurs
• Des services fournis par les chefs privés
• Des interruptions de service
• Des pertes de données

Notre responsabilité est limitée au montant payé pour nos services premium.`
      },
      {
        title: "9. Modification et résiliation",
        content: `MHARHER se réserve le droit de :
• Modifier ces conditions avec préavis de 30 jours
• Suspendre ou supprimer des comptes en cas de violation
• Modifier ou interrompre des fonctionnalités

Vous pouvez supprimer votre compte à tout moment via les paramètres.`
      },
      {
        title: "10. Droit applicable",
        content: `Ces conditions sont régies par le droit tunisien. Tout litige sera soumis aux tribunaux compétents de Tunis, Tunisie.

Pour toute question : legal@mharher.tn`
      }
    ]
  },
  privacy: {
    title: "Politique de Confidentialité",
    icon: Shield,
    lastUpdated: "1er Février 2026",
    sections: [
      {
        title: "1. Données collectées",
        content: `Nous collectons les données suivantes :

Données d'inscription :
• Nom, prénom, email
• Photo de profil (optionnel)
• Liens réseaux sociaux (optionnel)

Données d'utilisation :
• Recettes consultées et créées
• Interactions (likes, commentaires)
• Points BARKA et niveau
• Historique de connexion

Données de paiement (via Stripe) :
• Informations de facturation
• Historique des transactions`
      },
      {
        title: "2. Utilisation des données",
        content: `Vos données sont utilisées pour :
• Fournir et améliorer nos services
• Personnaliser votre expérience
• Calculer et afficher vos statistiques
• Traiter les paiements
• Envoyer des notifications (avec votre accord)
• Prévenir la fraude

Nous ne vendons JAMAIS vos données à des tiers.`
      },
      {
        title: "3. Partage des données",
        content: `Vos données peuvent être partagées avec :
• Stripe pour le traitement des paiements
• Nos hébergeurs cloud sécurisés
• Les autorités si requis par la loi

Données publiques visibles par tous :
• Nom d'utilisateur et photo de profil
• Recettes et posts publiés
• Points BARKA et badges
• Classements`
      },
      {
        title: "4. Sécurité",
        content: `Nous protégeons vos données par :
• Chiffrement SSL/TLS
• Hachage des mots de passe (bcrypt)
• Accès restreint aux bases de données
• Audits de sécurité réguliers

En cas de fuite de données, vous serez notifié sous 72h.`
      },
      {
        title: "5. Vos droits (RGPD/Loi tunisienne)",
        content: `Vous avez le droit de :
• Accéder à vos données personnelles
• Rectifier vos informations
• Supprimer votre compte et données
• Exporter vos données (portabilité)
• Retirer votre consentement
• Déposer une plainte

Contact : privacy@mharher.tn`
      },
      {
        title: "6. Cookies",
        content: `Nous utilisons des cookies pour :
• Maintenir votre session connectée
• Mémoriser vos préférences
• Analyser l'utilisation du site

Cookies essentiels : toujours actifs
Cookies analytiques : avec votre consentement

Vous pouvez gérer vos préférences dans les paramètres.`
      },
      {
        title: "7. Conservation des données",
        content: `Durée de conservation :
• Compte actif : données conservées
• Compte supprimé : 30 jours puis suppression
• Données de paiement : 10 ans (obligation légale)
• Logs de connexion : 1 an`
      },
      {
        title: "8. Transfert international",
        content: `Vos données peuvent être stockées sur des serveurs situés dans l'UE. Ces transferts sont encadrés par des clauses contractuelles garantissant un niveau de protection adéquat.`
      }
    ]
  },
  cookies: {
    title: "Politique des Cookies",
    icon: Cookie,
    lastUpdated: "1er Février 2026",
    sections: [
      {
        title: "Qu'est-ce qu'un cookie ?",
        content: `Un cookie est un petit fichier texte stocké sur votre appareil lors de la visite d'un site web. Il permet au site de mémoriser vos actions et préférences.`
      },
      {
        title: "Cookies utilisés par MHARHER",
        content: `Cookies essentiels (toujours actifs) :
• mharher_token : authentification
• mharher_preferences : langue, thème

Cookies analytiques (avec consentement) :
• Analyse de fréquentation
• Mesure de performance

Cookies tiers :
• Stripe : paiements sécurisés`
      },
      {
        title: "Gestion des cookies",
        content: `Vous pouvez gérer vos préférences :
• Dans les paramètres de MHARHER
• Dans les paramètres de votre navigateur

Note : désactiver certains cookies peut affecter le fonctionnement du site.`
      }
    ]
  }
};

export default function LegalPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(type || 'terms');
  
  const content = LEGAL_CONTENT[activeTab];
  const Icon = content?.icon || FileText;

  return (
    <div className="min-h-screen bg-[#F8F5F2]" data-testid="legal-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] text-white">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white hover:bg-white/10 mb-6"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
              <Icon className="w-7 h-7" />
            </div>
            <div>
              <h1 className="font-fraunces text-3xl font-bold">{content?.title}</h1>
              <p className="text-white/70">Dernière mise à jour : {content?.lastUpdated}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {Object.entries(LEGAL_CONTENT).map(([key, value]) => {
            const TabIcon = value.icon;
            return (
              <Button
                key={key}
                variant={activeTab === key ? 'default' : 'outline'}
                className={`rounded-full whitespace-nowrap ${
                  activeTab === key 
                    ? 'bg-[#1E4ED8] text-white' 
                    : 'bg-white text-gray-700'
                }`}
                onClick={() => {
                  setActiveTab(key);
                  navigate(`/legal/${key}`, { replace: true });
                }}
              >
                <TabIcon className="w-4 h-4 mr-2" />
                {key === 'terms' ? 'CGU' : key === 'privacy' ? 'Confidentialité' : 'Cookies'}
              </Button>
            );
          })}
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {content?.sections.map((section, index) => (
            <Card key={index} className="p-6 bg-white rounded-2xl">
              <h2 className="font-fraunces text-xl font-bold text-gray-900 mb-4">
                {section.title}
              </h2>
              <div className="prose prose-gray max-w-none">
                {section.content.split('\n').map((paragraph, i) => (
                  <p key={i} className="text-gray-600 whitespace-pre-line mb-2">
                    {paragraph}
                  </p>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Contact Section */}
        <Card className="mt-8 p-6 bg-gradient-to-br from-[#1E4ED8]/5 to-[#1E4ED8]/10 rounded-2xl border-[#1E4ED8]/20">
          <h3 className="font-fraunces text-xl font-bold mb-4">Nous contacter</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#1E4ED8]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">legal@mharher.tn</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#1E4ED8]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Adresse</p>
                <p className="font-medium">Tunis, Tunisie</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1E4ED8]/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#1E4ED8]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Téléphone</p>
                <p className="font-medium">+216 XX XXX XXX</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Footer links */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>
            En utilisant MHARHER, vous acceptez nos{' '}
            <Link to="/legal/terms" className="text-[#1E4ED8] hover:underline">CGU</Link>,{' '}
            <Link to="/legal/privacy" className="text-[#1E4ED8] hover:underline">Politique de confidentialité</Link> et{' '}
            <Link to="/legal/cookies" className="text-[#1E4ED8] hover:underline">Politique des cookies</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
